'use client';

import * as RadixToast from '@radix-ui/react-toast';
import { createContext, useContext, useState, ReactNode } from 'react';
import styles from './ToastContext.module.scss';
import { Toast, ToastProps } from '@/components/molecules/Toast';
import clsx from 'clsx';

export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

interface ToastContextValue {
  handleToastOpen: (options: ToastProps) => void;
}

const MAX_TOASTS = 3;

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within <ToastProvider />');
  }

  return { handleToastOpen: ctx.handleToastOpen };
};

export const ToastProvider = ({
  children,
  position = 'top-right',
}: {
  children: ReactNode;
  position?: ToastPosition;
}) => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const handleToastOpen = (options: ToastProps) => {
    const id = options.id || Math.random().toString(36).slice(2);

    setToasts((prev) => {
      const next = [...prev, { ...options, id }];

      if (next.length > MAX_TOASTS) {
        next.shift();
      }

      return next;
    });
  };

  const handleToastClose = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <RadixToast.Provider swipeDirection="right">
      <ToastContext.Provider value={{ handleToastOpen }}>
        {children}

        {toasts.map((t) => (
          <Toast key={t.id} {...t} onClose={() => handleToastClose(t.id!)} />
        ))}

        <RadixToast.Viewport className={clsx(styles.viewport, styles[position] || styles['bottom-right'])} />
      </ToastContext.Provider>
    </RadixToast.Provider>
  );
};
